import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import MotionIQStudio from "@/components/MotionIQStudio";
import PoseExtractor from "@/components/PoseExtractor";
import { Video, Plus, Cpu, Loader2, GitCompare, X } from "lucide-react";

/**
 * MotionIQStudioPage
 * ──────────────────
 * Coach-facing container for the Spatial Fusion Studio. Lists this
 * team's captures on the left, hosts the Studio on the right, and
 * offers a "New capture" flow that lets the coach seed a capture from
 * an already-hosted video URL (e.g. a Cloudinary/S3 upload elsewhere in
 * the platform). File-upload UX lands with Phase 2.
 */
export default function MotionIQStudioPage() {
  const { captureId: routeCaptureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [captures, setCaptures] = useState([]);
  const [modes, setModes] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [me, setMe] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [telemetryEvents, setTelemetryEvents] = useState([]);
  const [newCap, setNewCap] = useState({
    video_url: "", label: "", mode: "pitching", athlete_user_id: "", video_fps: 60, duration_ms: 5000,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, m, meRes] = await Promise.all([
        api.get("/motioniq/captures"),
        api.get("/motioniq/sport-modes"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setCaptures(c.data?.captures || []);
      setModes(m.data?.modes || {});
      setMe(meRes.data);
      // Auto-select via route or first capture
      const target = routeCaptureId
        ? (c.data?.captures || []).find((x) => x.capture_id === routeCaptureId)
        : (c.data?.captures || [])[0];
      if (target) loadCapture(target.capture_id);
    } catch (e) {
      toast.error("Could not load MotionIQ Studio");
    } finally {
      setLoading(false);
    }
  };

  const loadCapture = async (id) => {
    try {
      const { data } = await api.get(`/motioniq/captures/${id}`);
      setActive(data);
      navigate(`/dashboard/motioniq/${id}${location.search}`, { replace: true });
      // Pull external telemetry (Blast/HitTrax) for this athlete so the
      // Studio's Live-telemetry graph can flag those swings.
      if (data?.athlete_user_id) {
        try {
          const t = await api.get(`/motioniq/telemetry-events?athlete_user_id=${data.athlete_user_id}&limit=25`);
          setTelemetryEvents(t.data?.events || []);
        } catch { setTelemetryEvents([]); }
      }
    } catch { /* silent */ }
  };

  // Query-string driven deep-link: /dashboard/motioniq/{id}?event=release_point
  // Compute the initial jump target from the URL so the Studio can seed
  // its scrubber at mount time (avoids a race with the child's reset).
  const initialJumpToMs = useMemo(() => {
    if (!active?.events?.length) return 0;
    const p = new URLSearchParams(location.search);
    const evName = p.get("event");
    if (!evName) return 0;
    const ev = active.events.find((e) => e.event === evName);
    return ev?.timestamp_ms ?? 0;
  }, [active?.capture_id, active?.events, location.search]);

  useEffect(() => { load(); }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Default athlete = the caller (coach can override for a roster player later)
      const meRes = await api.get("/auth/me");
      const payload = {
        ...newCap,
        athlete_user_id: newCap.athlete_user_id || meRes.data?.user_id,
        duration_ms: parseInt(newCap.duration_ms, 10),
        video_fps: parseInt(newCap.video_fps, 10),
      };
      const { data } = await api.post("/motioniq/captures", payload);
      toast.success("Capture created — Phase 2 pose extraction pending");
      setNewCap({ ...newCap, video_url: "", label: "" });
      await load();
      loadCapture(data.capture_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create capture");
    } finally {
      setCreating(false);
    }
  };

  const seedDemo = async (mode) => {
    setCreating(true);
    try {
      const { data } = await api.post("/motioniq/captures/demo", { mode });
      toast.success(`${mode === "hitting" ? "Hitting" : "Pitching"} demo seeded`);
      await load();
      loadCapture(data.capture.capture_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not seed demo");
    } finally {
      setCreating(false);
    }
  };

  // Toggle a capture in the compare basket (max 4). Loading is triggered
  // separately by the "Compare now" button so the coach can stage picks.
  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) {
        toast.error("Comparison limit is 4 captures");
        return prev;
      }
      return [...prev, id];
    });
  };

  const runCompare = async () => {
    if (compareIds.length < 2) {
      toast.error("Pick at least 2 captures");
      return;
    }
    try {
      const { data } = await api.get(`/motioniq/compare?capture_ids=${compareIds.join(",")}`);
      setCompareData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Compare failed");
    }
  };
  const clearCompare = () => { setCompareIds([]); setCompareData(null); };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="motioniq-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
          <Cpu className="h-3.5 w-3.5 text-fuchsia-400" /> MotionIQ · Phase 1
        </div>
        <h1 className="mt-1 font-display font-black text-3xl md:text-4xl tracking-tight text-white">
          Spatial Fusion Studio
        </h1>
        <p className="mt-2 text-sm text-white/60 max-w-2xl">
          Video + 3D joint vectors + continuous telemetry — one timeline. Sport-mode aware. Scrub any frame; every layer moves in lockstep.
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        {/* Captures rail */}
        <aside className="md:col-span-3 space-y-3">
          <div className="ink-card rounded-xl p-4">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 mb-3 flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5" /> Captures
            </div>
            {loading ? (
              <div className="text-white/40 text-sm flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading
              </div>
            ) : captures.length === 0 ? (
              <div className="text-white/40 text-sm">No captures yet. Add one below.</div>
            ) : (
              <ul className="space-y-1.5" data-testid="motioniq-captures-list">
                {captures.map((c) => {
                  const isActive = active?.capture_id === c.capture_id;
                  const inCompare = compareIds.includes(c.capture_id);
                  return (
                    <li key={c.capture_id} className="flex items-stretch gap-1.5">
                      <button
                        onClick={() => loadCapture(c.capture_id)}
                        className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors ${
                          isActive
                            ? "border-red-500/50 bg-red-500/10"
                            : "border-white/8 bg-white/[0.02] hover:border-white/20"
                        }`}
                        data-testid={`capture-item-${c.capture_id}`}
                      >
                        <div className="text-sm text-white font-semibold truncate">{c.label}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
                          {modes?.[c.mode]?.label || c.mode} · {c.frame_count || 0} frames
                        </div>
                      </button>
                      <button
                        onClick={() => toggleCompare(c.capture_id)}
                        title={inCompare ? "Remove from compare" : "Add to compare"}
                        className={`w-8 rounded-lg border text-[10px] font-bold transition-colors ${
                          inCompare
                            ? "border-fuchsia-400/50 bg-fuchsia-400/15 text-fuchsia-200"
                            : "border-white/10 text-white/40 hover:text-white hover:border-white/25"
                        }`}
                        data-testid={`capture-compare-${c.capture_id}`}
                      >
                        {inCompare ? "✓" : "+"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* New-capture form */}
          <form onSubmit={submitNew} className="ink-card rounded-xl p-4 space-y-3" data-testid="motioniq-new-form">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> New capture
            </div>
            <div>
              <Label className="text-white/70 text-xs">Video URL</Label>
              <Input required value={newCap.video_url} onChange={(e) => setNewCap({ ...newCap, video_url: e.target.value })}
                placeholder="https://…/pitch.mp4"
                className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white text-xs"
                data-testid="motioniq-new-video-url"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Label</Label>
              <Input value={newCap.label} onChange={(e) => setNewCap({ ...newCap, label: e.target.value })}
                placeholder="Jake · bullpen · 3/12"
                className="mt-1.5 bg-[var(--ink-1)] border-white/15 text-white text-xs"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Sport mode</Label>
              <select value={newCap.mode} onChange={(e) => setNewCap({ ...newCap, mode: e.target.value })}
                className="mt-1.5 h-9 w-full border border-white/15 rounded-md px-2 text-xs bg-[var(--ink-1)] text-white"
                data-testid="motioniq-new-mode"
              >
                {modes && Object.entries(modes).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-white/70 text-[10px]">FPS</Label>
                <Input type="number" value={newCap.video_fps}
                  onChange={(e) => setNewCap({ ...newCap, video_fps: e.target.value })}
                  className="mt-1 bg-[var(--ink-1)] border-white/15 text-white text-xs" />
              </div>
              <div>
                <Label className="text-white/70 text-[10px]">Duration ms</Label>
                <Input type="number" value={newCap.duration_ms}
                  onChange={(e) => setNewCap({ ...newCap, duration_ms: e.target.value })}
                  className="mt-1 bg-[var(--ink-1)] border-white/15 text-white text-xs" />
              </div>
            </div>
            <Button type="submit" disabled={creating}
              className="w-full text-white font-bold text-xs"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="motioniq-new-submit"
            >
              {creating ? "Creating…" : "Create capture"}
            </Button>
          </form>

          {/* Compare basket */}
          {compareIds.length > 0 && (
            <div className="ink-card rounded-xl p-4 space-y-2" data-testid="motioniq-compare-basket">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
                <GitCompare className="h-3.5 w-3.5 text-fuchsia-400" /> Compare · {compareIds.length}/4
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={runCompare} disabled={compareIds.length < 2}
                  className="flex-1 text-white text-[11px] font-bold"
                  style={{ backgroundColor: "var(--brand-secondary)" }}
                  data-testid="motioniq-compare-run">
                  Compare now
                </Button>
                <Button size="sm" onClick={clearCompare} variant="ghost"
                  className="text-white/60 text-[11px]"
                  data-testid="motioniq-compare-clear">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Pose extractor (Phase 2) */}
          <PoseExtractor
            athleteUserId={me?.user_id}
            mode={newCap.mode}
            onCaptureReady={async (capId) => { await load(); loadCapture(capId); }}
          />

          {/* Demo seeder */}
          <div className="ink-card rounded-xl p-4 space-y-2" data-testid="motioniq-demo-seeder">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
              <Cpu className="h-3.5 w-3.5 text-fuchsia-400" /> Insert demo capture
            </div>
            <p className="text-[11px] text-white/50 leading-snug">
              Synthetic pose + telemetry — see every Studio layer without waiting on ML.
            </p>
            <div className="flex gap-2">
              <Button size="sm" disabled={creating}
                onClick={() => seedDemo("pitching")}
                className="flex-1 text-white text-[11px] font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.09]"
                data-testid="motioniq-seed-pitching">
                Pitching
              </Button>
              <Button size="sm" disabled={creating}
                onClick={() => seedDemo("hitting")}
                className="flex-1 text-white text-[11px] font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.09]"
                data-testid="motioniq-seed-hitting">
                Hitting
              </Button>
            </div>
          </div>
        </aside>

        {/* Studio */}
        <main className="md:col-span-9">
          {compareData ? (
            <CompareView
              data={compareData}
              onClose={() => setCompareData(null)}
            />
          ) : active ? (
            <MotionIQStudio
              capture={active}
              sportConfig={active.sport_config}
              telemetryEvents={telemetryEvents}
              initialJumpToMs={initialJumpToMs}
            />
          ) : (
            <div className="ink-card rounded-xl p-10 text-center text-white/50" data-testid="motioniq-empty-state">
              <Cpu className="h-8 w-8 mx-auto text-white/25 mb-3" />
              <div className="text-white font-display font-black text-xl mb-1">No capture selected</div>
              <div className="text-sm">Create one on the left or pick from the list to open the studio.</div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ── CompareView ───────────────────────────────────────────────────────
// Renders 2-4 captures' skeletons in different tint colors on a shared
// stage so a coach can spot mechanical differences frame-by-frame.
const COMPARE_TINTS = ["#22D3EE", "#F59E0B", "#F43F5E", "#A78BFA"];
const BONES_CMP = [
  ["shoulder_l", "shoulder_r"], ["hip_l", "hip_r"],
  ["shoulder_l", "hip_l"], ["shoulder_r", "hip_r"],
  ["shoulder_l", "elbow_l"], ["elbow_l", "wrist_l"],
  ["shoulder_r", "elbow_r"], ["elbow_r", "wrist_r"],
  ["hip_l", "knee_l"], ["knee_l", "ankle_l"],
  ["hip_r", "knee_r"], ["knee_r", "ankle_r"],
];

function CompareView({ data, onClose }) {
  const [ms, setMs] = useState(0);
  const maxDuration = Math.max(...data.captures.map((c) => c.duration_ms || (c.frames?.at(-1)?.timestamp_ms ?? 5000)));

  return (
    <section className="ink-card rounded-2xl p-4 space-y-4" data-testid="motioniq-compare-view">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/45 flex items-center gap-1.5">
            <GitCompare className="h-3.5 w-3.5 text-fuchsia-400" /> Compare · {data.captures.length} captures
          </div>
          <div className="mt-1 font-display font-black text-lg text-white">
            {data.captures.map((c) => c.label).join(" · ")}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} className="text-white/60"
          data-testid="motioniq-compare-close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}
        data-testid="motioniq-compare-stage">
        <svg viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 w-full h-full">
          <defs>
            <linearGradient id="cmpGrid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(34,211,238,0.05)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0.35)" />
            </linearGradient>
          </defs>
          {/* Ice-blue horizon */}
          <line x1="0" y1="300" x2="1280" y2="300" stroke="rgba(34,211,238,0.15)"
                strokeWidth="1" strokeDasharray="4 6" />
          {/* Vertical converging lines */}
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <line key={`v-${i}`} x1={i * 160} y1={720} x2={640} y2={300}
                  stroke="url(#cmpGrid)" strokeWidth="1" />
          ))}
          {/* Each capture's current-frame skeleton */}
          {data.captures.map((cap, idx) => {
            const frames = cap.frames || [];
            if (!frames.length) return null;
            // Nearest frame by proportional timestamp
            const capDur = cap.duration_ms || (frames.at(-1)?.timestamp_ms ?? 5000);
            const targetMs = (ms / maxDuration) * capDur;
            let best = frames[0], bestDelta = Math.abs(best.timestamp_ms - targetMs);
            for (const f of frames) {
              const d = Math.abs(f.timestamp_ms - targetMs);
              if (d < bestDelta) { best = f; bestDelta = d; }
            }
            const pts = best?.joint_pixels_2d || {};
            const color = COMPARE_TINTS[idx % COMPARE_TINTS.length];
            return (
              <g key={cap.capture_id} opacity="0.92">
                {BONES_CMP.map(([a, b]) => {
                  const pa = pts[a], pb = pts[b];
                  if (!pa || !pb) return null;
                  return (
                    <line key={`${cap.capture_id}-${a}-${b}`}
                      x1={pa[0]} y1={pa[1]} x2={pb[0]} y2={pb[1]}
                      stroke={color} strokeWidth="3" strokeLinecap="round" />
                  );
                })}
                {Object.entries(pts).slice(0, 20).map(([k, [x, y]]) => (
                  <circle key={`${cap.capture_id}-${k}`} cx={x} cy={y} r="4"
                    fill={color} opacity="0.85" />
                ))}
              </g>
            );
          })}
          {/* Legend */}
          <g transform="translate(24, 24)">
            <rect x="0" y="0" width="200" height={20 + data.captures.length * 20}
              rx="8" fill="rgba(10,20,36,0.7)" stroke="rgba(255,255,255,0.08)" />
            {data.captures.map((cap, idx) => (
              <g key={`lg-${cap.capture_id}`} transform={`translate(10, ${18 + idx * 20})`}>
                <rect x="0" y="-6" width="14" height="6" rx="2"
                  fill={COMPARE_TINTS[idx % COMPARE_TINTS.length]} />
                <text x="22" y="0" fill="rgba(255,255,255,0.85)"
                  fontSize="11" fontFamily="monospace" fontWeight="700">
                  {(cap.label || cap.capture_id).slice(0, 22)}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>

      <div className="flex items-center gap-3">
        <input type="range" min={0} max={maxDuration} step={16} value={ms}
          onChange={(e) => setMs(parseInt(e.target.value, 10))}
          className="flex-1 accent-fuchsia-500"
          data-testid="motioniq-compare-scrubber" />
        <span className="text-[11px] font-mono-num text-white/60 tabular-nums w-24 text-right">
          {(ms / 1000).toFixed(2)}s / {(maxDuration / 1000).toFixed(2)}s
        </span>
      </div>

      {/* Metric comparison table */}
      <div className="overflow-x-auto" data-testid="motioniq-compare-metrics">
        <table className="min-w-full text-[11px]">
          <thead>
            <tr className="text-white/40 uppercase tracking-[0.15em]">
              <th className="text-left font-semibold py-2 px-3">Metric</th>
              {data.captures.map((cap, idx) => (
                <th key={cap.capture_id} className="text-right font-mono-num py-2 px-3"
                  style={{ color: COMPARE_TINTS[idx % COMPARE_TINTS.length] }}>
                  {(cap.label || cap.capture_id).slice(0, 20)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-white/80">
            {compareMetricKeys(data.captures).map((key) => (
              <tr key={key} className="border-t border-white/8">
                <td className="py-2 px-3 uppercase tracking-[0.1em] text-white/60">{key.replace(/_/g, " ")}</td>
                {data.captures.map((cap) => {
                  const val = peakOf(cap, key);
                  return (
                    <td key={`${cap.capture_id}-${key}`}
                      className="text-right font-mono-num py-2 px-3 tabular-nums">
                      {val === null ? "—" : val.toFixed(1)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function compareMetricKeys(caps) {
  const keys = new Set();
  caps.forEach((c) => {
    (c.frames || []).forEach((f) => {
      Object.keys(f.live_telemetry || {}).forEach((k) => keys.add(k));
    });
  });
  return [...keys].slice(0, 10);
}

function peakOf(cap, key) {
  let max = null;
  (cap.frames || []).forEach((f) => {
    const v = Number(f.live_telemetry?.[key]);
    if (!Number.isNaN(v)) max = max === null ? v : Math.max(max, v);
  });
  return max;
}

